import styled from 'styled-components/macro';
import classnames from 'classnames';

import Step from '@mui/material/Step';
import StepLabel, { StepLabelProps } from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import StepConnector from '@mui/material/StepConnector';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import { BaseReactComponent } from '../../../types';

export type Step = {
  /**
   * Step main label.
   */
  label: string;
  /**
   * Step secondary label displayed under main label.
   */
  secondaryLabel?: string;
  /**
   * If `true`, set step in error.
   */
  error?: boolean;
};

export interface HiStepperProps extends BaseReactComponent {
  /**
   * List of steps.
   *
   * Step: `{
   *   label: string;
   *   secondaryLabel?: string;
   *   error?: boolean;
   * }`
   */
  steps: Step[];
  /**
   * Set the active step (zero based index).
   */
  activeStep: number;
  /**
   * Callback when a step a previous is clicked. Go to this step when clicked.
   */
  onStepClick?: (stepIndex: number) => void;
  /**
   * If `true`, disable click on every step.
   */
  disabled?: boolean;
}

/**
 * Steppers convey **progress through numbered steps**. It provides a wizard-like workflow.
 *
 * Horizontal steppers are ideal when the contents of one step depend on an earlier step.
 *
 * - Avoid using long step names in horizontal steppers.
 * - Avoid using more than 5 steps.
 * - It's great to implement `onStepClick` to go to a previous step by clicking on it.
 */
const HiStepper = ({
  steps,
  activeStep,
  onStepClick,
  disabled = false,
  ...other
}: HiStepperProps) => {
  const handleStepClick = (stepIndex: number) => {
    if (disabled || !onStepClick) {
      return;
    }

    // Call parent only when previous step is clicked
    if (stepIndex < activeStep) {
      onStepClick(stepIndex);
    }
  };

  return (
    <StyledStepper
      {...other}
      activeStep={activeStep}
      alternativeLabel
      connector={
        <StepConnector
          classes={{
            line: 'HiStepper-lineConnector',
            active: 'HiStepper-activeConnector',
            completed: 'HiStepper-completedConnector'
          }}
        />
      }
    >
      {steps.map(({ label, secondaryLabel, error }, index) => {
        // If step in error, add props
        const labelProps = {} as StepLabelProps;
        if (error) {
          labelProps.error = true;
          labelProps.icon = (
            <StepErrorRoot>
              <ErrorIcon fontSize="medium" />
            </StepErrorRoot>
          );
        }

        return (
          <Step
            key={label}
            className={classnames('HiStepper-stepRoot', {
              // Step clickable if not disabled and it is a completed step
              'HiStepper-stepClickable': !disabled && index < activeStep,
              'HiStepper-stepError': error
            })}
            onClick={() => handleStepClick(index)}
            disabled={disabled}
          >
            <StepLabel
              classes={{
                labelContainer: 'HiStepper-stepLabel-Container',
                label: 'HiStepper-stepLabel',
                active: 'HiStepper-stepLabel-Active',
                completed: 'HiStepper-stepLabel-Complete'
              }}
              StepIconProps={{
                classes: {
                  root: 'HiStepper-stepIcon',
                  text: 'HiStepper-stepIcon-Text',
                  completed: 'HiStepper-stepIcon-Completed',
                  active: 'HiStepper-stepIcon-Active'
                }
              }}
              optional={
                secondaryLabel && (
                  <Typography variant="caption">{secondaryLabel}</Typography>
                )
              }
              {...labelProps}
            >
              {label}
            </StepLabel>
          </Step>
        );
      })}
    </StyledStepper>
  );
};

const StepErrorRoot = styled('div')`
  background-color: ${({ theme }) => theme.palette.error.main};
  color: #fff;
  width: 24px;
  height: 24px;
  display: flex;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
`;

const StyledStepper = styled(Stepper)`
  background-color: transparent;
  justify-content: center;

  .HiStepper-stepRoot {
    max-width: 250px;
  }

  .HiStepper-stepClickable {
    cursor: pointer;
  }

  .HiStepper-lineConnector {
    border-top-width: 2px;
    border-color: ${(props) => props.theme.palette.grey[400]};
  }
  .HiStepper-activeConnector {
    .HiStepper-lineConnector {
      border-color: ${(props) => props.theme.palette.primary.main};
    }
  }
  .HiStepper-completedConnector {
    .HiStepper-lineConnector {
      border-color: ${(props) => props.theme.palette.success.main};
    }
  }
  .HiStepper-stepError {
    .HiStepper-lineConnector {
      border-color: ${(props) => props.theme.palette.error.main};
    }
    .HiStepper-stepIcon {
      color: ${(props) => props.theme.palette.error.main};
    }
  }

  .HiStepper-stepLabel-Container {
    text-align: center;
  }
  .HiStepper-stepLabel {
    margin-top: 8px;
    ${(props) => props.theme.typography.subtitle2};
    color: ${(props) => props.theme.palette.text.secondary};

    &-Active {
      color: ${(props) => props.theme.palette.text.primary};
    }
    &-Complete {
      border-color: ${(props) => props.theme.palette.success.main};
      color: ${(props) => props.theme.palette.text.primary};
    }
  }

  .HiStepper-stepIcon {
    color: ${(props) => props.theme.palette.grey[400]};
    &-Completed {
      color: ${(props) => props.theme.palette.success.main};
    }
    &-Active {
      color: ${(props) => props.theme.palette.primary.main};
    }
    &-Text {
      font-size: 11px;
      font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    }
  }
`;

export default HiStepper;
